import {Component, OnInit} from '@angular/core';
import {DialogRef} from '@progress/kendo-angular-dialog';
import {MultiSelectComponent} from '@progress/kendo-angular-dropdowns';

export interface AvailableServiceOption {
    service: string;
    market: string;
    // Precomputed for the multiselect's textField, rather than an item template - "SE2W1204
    // - VIC - MDE - AFK" tells the user which market a service is currently coming from.
    label: string;
}

// Per-service mode's "Add Market" AND "Edit Market" (currentName '' vs. populated - same
// dual-purpose pattern RenameMarketDialogComponent already uses for ranges mode) - unlike
// the bare name prompt that dialog offers, this lets a market's RSID list be built or grown
// two ways at once: services pulled straight out of the Other/unassigned pool, and services
// moved in from other existing markets - instead of always typing RSIDs in one at a time.
@Component({
    selector: 'app-add-service-market-dialog',
    templateUrl: './add-service-market-dialog.component.html',
    styleUrls: ['./add-service-market-dialog.component.scss']
})
export class AddServiceMarketDialogComponent implements OnInit {
    // Set by the caller right after dialogService.open(), before the dialog is shown. Plain
    // properties, not getters - Kendo's [data] binding needs a stable array reference; a
    // getter recomputing a brand-new array (and brand-new item objects) on every change
    // detection cycle broke its internal selection matching, so clicking an item never
    // visibly selected it. These stay the full, untouched source lists - the two filtered*
    // properties below are what's actually bound to [data].
    existingNames: string[] = [];
    unassignedOptions: {service: string}[] = [];
    availableServices: AvailableServiceOption[] = [];
    // '' for Add Market; the market's current name for Edit Market - drives both the name
    // field's starting value and the uniqueness check (a market can keep its own name).
    currentName = '';
    confirmLabel = 'Add';

    name = '';
    selectedUnassigned: string[] = [];
    selectedFromOtherMarkets: string[] = [];

    // The multiselects' own visible lists, narrowed by typing a partial RSID - reset back to
    // the full source list after each selection (matching legacy-business-rules/business-
    // rules-priorities/save-priority-dialog's own filterable-multiselect pattern), so the
    // next search starts from the full list rather than whatever was already filtered down.
    filteredUnassignedOptions: {service: string}[] = [];
    filteredAvailableServices: AvailableServiceOption[] = [];

    constructor(private dialogRef: DialogRef) {
    }

    ngOnInit(): void {
        this.name = this.currentName;
        this.filteredUnassignedOptions = this.unassignedOptions;
        this.filteredAvailableServices = this.availableServices;
    }

    public onUnassignedFilter(value: string): void {
        const term = value.toLowerCase();
        this.filteredUnassignedOptions = this.unassignedOptions.filter(option => option.service.toLowerCase().includes(term));
    }

    public onUnassignedValueChange(multiSelect: MultiSelectComponent): void {
        multiSelect.clearFilter();
        this.filteredUnassignedOptions = this.unassignedOptions;
    }

    public onAvailableFilter(value: string): void {
        const term = value.toLowerCase();
        this.filteredAvailableServices = this.availableServices.filter(option =>
            option.service.toLowerCase().includes(term) || option.market.toLowerCase().includes(term));
    }

    public onAvailableValueChange(multiSelect: MultiSelectComponent): void {
        multiSelect.clearFilter();
        this.filteredAvailableServices = this.availableServices;
    }

    public get error(): string | null {
        const trimmed = this.name.trim();
        if (!trimmed) {
            return 'Enter a name.';
        }
        if (trimmed === 'Other') {
            return '"Other" is reserved.';
        }
        if (trimmed !== this.currentName && this.existingNames.includes(trimmed)) {
            return 'A market with this name already exists.';
        }
        return null;
    }

    public onCancel(): void {
        this.dialogRef.close({confirmed: false});
    }

    public onAdd(): void {
        if (this.error) {
            return;
        }
        this.dialogRef.close({
            confirmed: true,
            name: this.name.trim(),
            selectedUnassigned: this.selectedUnassigned,
            selectedFromOtherMarkets: this.selectedFromOtherMarkets
        });
    }
}
